import { BreadcrumbsComponent as UdBreadcrumbsComponent } from '@accesso/ng-uds';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { filter, map, Subject, takeUntil } from 'rxjs';
import { ProductService } from '../../core/services/product.service';
import { NavigationProduct } from '../../shared/models/product.model';

@Component({
  selector: 'app-breadcrumbs',
  standalone: true,
  imports: [UdBreadcrumbsComponent],
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
})
export class BreadcrumbsComponent implements OnInit, OnDestroy {
  static readonly ROUTE_DATA_BREADCRUMB = 'breadcrumb';
  menuItems: MenuItem[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private productService: ProductService
  ) {}

  ngOnInit(): void {
    this.processCurrentRoute();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.processCurrentRoute();
      });
  }

  private processCurrentRoute(): void {
    const url = this.router.url;

    // If we're on a product/page route, fetch the current page info
    if (url.startsWith('/portal/') && url !== '/portal') {
      this.productService
        .getNavigationProducts()
        .pipe(map((products) => this.productService.flattenNavigationProducts(products)))
        .subscribe((products) => {
          this.buildDynamicBreadcrumbs(products, url);
        });
    }
  }

  private buildDynamicBreadcrumbs(products: NavigationProduct[], currentUrl: string): void {
    this.menuItems = [{ label: 'Products', routerLink: '/portal' }];

    const path = currentUrl.startsWith('/portal/') ? currentUrl.substring('/portal/'.length) : '';

    if (path) {
      // Split the path into segments (e.g., "mobile-sdk/overview" -> ["mobile-sdk", "overview"])
      const pathSegments = path.split('/');

      // First segment should match a product route
      if (pathSegments.length > 0) {
        const productRoute = pathSegments[0];

        pathSegments.forEach((segment) => {
          const product = this.findProductByRoute(products, `/${segment}`);
          if (product) {
            // If we have a page segment, combine product and page name
            if (product.pages) {
              const page = product.pages.find((page) => {
                return page.fullRoute === `/${path}`;
              });

              if (page) {
                // Add combined product and page name as a single breadcrumb
                this.menuItems.push({
                  label: `${product.name} - ${page.name}`,
                  routerLink: page.fullRoute,
                });
              } else {
                this.menuItems.push({
                  label: product.name,
                  routerLink: `/portal/${productRoute}`,
                });
              }
            } else {
              this.menuItems.push({
                label: product.name,
                routerLink: `/portal/${productRoute}`,
              });
            }
          }
        });
      }
    }
  }

  private findProductByRoute(products: NavigationProduct[], route: string): NavigationProduct | null {
    for (const product of products) {
      if (product.route === route) {
        return product;
      }
    }

    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
